/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './PreferencesPeople.scss';

import { isEmpty } from 'lodash';
import { bool, object, string } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as ProfileIcon } from 'assets/icons/profile.svg';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesSectionHeader from 'components/layouts/Preferences/shared/PreferencesSectionHeader';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesRowLabel from 'components/layouts/Preferences/shared/PreferencesRowLabel';
import Content from 'services/Content';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { formatPhoneNumber } from 'services/Formats';
import {
  getCustomerName,
  getDaytimePhoneNumber,
  getEveningPhoneNumber,
  getMobilePhoneNumber,
  getFaxNumber,
  getPrimaryMailingAddress,
  getPrimaryEmailAddress,
  hasResidentialAddress,
  getResidentialAddress
} from 'state/customer/CustomerSelectors';
import {
  getView,
  canViewBeneficiaries,
  canViewTrustedContacts,
  canViewContactInformation
} from 'state/view/ViewSelectors';


const Address = ({ address }) => {
  const { addressLines, city, country, state, zipCode } = address;
  let cityStateZip = '';
  if (city || state || zipCode) {
    const cityState = [city, state].join(', ').trim();
    cityStateZip = `${cityState} ${zipCode}`;
  }

  return (
    <Fragment>
      {addressLines &&
        addressLines.map((addressLine, idx) => (
          <div className="address" key={idx}>
            <AddressLineWithPermissions
              addressLine={addressLine}
              styleOverride={{ fontFamily: 'Nimbus Sans Bold' }}
              shouldMask
            />
          </div>
        ))}
      {cityStateZip && (
        <div className="address">
          <CityStateZipWithPermissions
            cityStateZip={cityStateZip}
            styleOverride={{ fontFamily: 'Nimbus Sans Bold' }}
            shouldMask
          />
        </div>
      )}
      {country && (
        <div className="address">
          <CountryWithPermissions
            country={country}
            styleOverride={{ fontFamily: 'Nimbus Sans Bold' }}
            shouldMask
          />
        </div>
      )}
    </Fragment>
  );
};

Address.propTypes = {
  address: object.isRequired
};

const AddressLine = ({ addressLine }) => <Fragment>{addressLine}</Fragment>;

const CityStateZip = ({ cityStateZip }) => <Fragment>{cityStateZip}</Fragment>;

const Country = ({ country }) => <Fragment>{country}</Fragment>;

const OwnerName = ({ name }) => (
  <span className="contact-info-row-value">{name}</span>
);

const Email = ({ email }) => (
  <span className="contact-info-row-value">{email.address}</span>
);

const DaytimePhone = ({ daytimePhoneNumber }) => (
  <span className="contact-info-row-value">{daytimePhoneNumber}</span>
);

const EveningPhone = ({ eveningPhoneNumber }) => (
  <span className="contact-info-row-value">{eveningPhoneNumber}</span>
);

const MobilePhone = ({ mobilePhoneNumber }) => (
  <span className="contact-info-row-value">{mobilePhoneNumber}</span>
);

const FaxNumber = ({ faxNumber }) => (
  <span className="contact-info-row-value">{faxNumber}</span>
);

const AddressLineWithPermissions = WithUserPermissions(AddressLine);
const CityStateZipWithPermissions = WithUserPermissions(CityStateZip);
const CountryWithPermissions = WithUserPermissions(Country);
const OwnerNameWithPermissions = WithUserPermissions(OwnerName);
const EmailWithPermissions = WithUserPermissions(Email);
const DaytimePhoneNumberWithPermissions = WithUserPermissions(DaytimePhone);
const EveningPhoneNumberWithPermissions = WithUserPermissions(EveningPhone);
const MobilePhoneNumberWithPermissions = WithUserPermissions(MobilePhone);
const FaxNumberWithPermissions = WithUserPermissions(FaxNumber);

const overridePeopleStyle = {
  display: 'flex',
  flex: 2
};

const DesktopPeople = ({
  address,
  canViewBeneficiaries,
  canViewTrustedContacts,
  canViewContactInformation,
  daytimePhoneNumber,
  eveningPhoneNumber,
  email,
  faxNumber,
  mobilePhoneNumber,
  name,
  hasResidential,
  residentialAddress = {}
}) => (
  <Fragment>
    <PreferencesRow>
      <PreferencesCol col={3}>
        <PreferencesRowLabel label="Contact Information" />
      </PreferencesCol>

      <PreferencesCol col={6}>
        <div className="contact-info-row">
          <span className="contact-info-row-label">Owner name</span>
          <OwnerNameWithPermissions
            name={name}
            styleOverride={overridePeopleStyle}
            shouldMask
          />
        </div>
        {hasResidential && !isEmpty(residentialAddress) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">
              Residential/Legal Address
            </span>
            <span className="contact-info-row-value">
              <Address address={residentialAddress} />
            </span>
          </div>
        )}
        {!isEmpty(address) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">Mailing Address</span>
            <span className="contact-info-row-value">
              {hasResidential && address.sameAsResidential ? (
                `Same as residential/legal address`
              ) : (
                <Address address={address} />
              )}
            </span>
          </div>
        )}
        {!isEmpty(email) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">Email</span>
            <EmailWithPermissions
              email={email}
              styleOverride={overridePeopleStyle}
              shouldMask
            />
          </div>
        )}
        {!isEmpty(daytimePhoneNumber) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">Daytime phone number</span>
            <DaytimePhoneNumberWithPermissions
              daytimePhoneNumber={formatPhoneNumber(daytimePhoneNumber)}
              styleOverride={overridePeopleStyle}
              shouldMask
            />
          </div>
        )}
        {!isEmpty(eveningPhoneNumber) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">Evening phone number</span>
            <EveningPhoneNumberWithPermissions
              eveningPhoneNumber={formatPhoneNumber(eveningPhoneNumber)}
              styleOverride={overridePeopleStyle}
              shouldMask
            />
          </div>
        )}
        {!isEmpty(mobilePhoneNumber) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">Mobile phone number</span>
            <MobilePhoneNumberWithPermissions
              mobilePhoneNumber={formatPhoneNumber(mobilePhoneNumber)}
              styleOverride={overridePeopleStyle}
              shouldMask
            />
          </div>
        )}
        {!isEmpty(faxNumber) && (
          <div className="contact-info-row">
            <span className="contact-info-row-label">Fax number</span>
            <FaxNumberWithPermissions
              faxNumber={formatPhoneNumber(faxNumber)}
              styleOverride={overridePeopleStyle}
              shouldMask
            />
          </div>
        )}
      </PreferencesCol>

      <PreferencesCol col={3}>
        {canViewContactInformation ? (
          <Link
            className="preferences-people-link"
            id="e2e-profile-update-contact-info-link"
            to="/profile/contact-information">
            <span>Update Contact Information</span>
          </Link>
        ) : (
          <span
            className="preferences-people-warning-text"
            dangerouslySetInnerHTML={{
              __html: Content.profileUpdateContactInfo
            }}
          />
        )}
      </PreferencesCol>
    </PreferencesRow>
    {canViewTrustedContacts && (
      <PreferencesRow>
        <PreferencesCol col={3}>
          <PreferencesRowLabel label="Trusted Contacts" />
        </PreferencesCol>

        <PreferencesCol col={6}>
          <div className="preferences-people-text">
            {Content.trustedContactInfo}
          </div>
        </PreferencesCol>

        <PreferencesCol col={3}>
          <Link
            className="preferences-people-link"
            id="e2e-profile-trusted-contacts-link"
            to="/profile/trusted-contacts">
            <span>Add/Update Trusted Contacts</span>
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}

    {canViewBeneficiaries && (
      <PreferencesRow>
        <PreferencesCol col={3}>
          <PreferencesRowLabel label="Beneficiary on File" />
        </PreferencesCol>

        <PreferencesCol col={6}>
          <div className="preferences-people-text">
            {Content.beneficiaryInfo}
          </div>
        </PreferencesCol>

        <PreferencesCol col={3}>
          <Link
            className="preferences-people-link"
            id="e2e-profile-beneficiaries-link"
            to="/profile/beneficiaries">
            <span>Update Beneficiary on File</span>
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
  </Fragment>
);

DesktopPeople.propTypes = {
  address: object.isRequired,
  canViewBeneficiaries: bool.isRequired,
  canViewTrustedContacts: bool.isRequired,
  daytimePhoneNumber: object,
  email: object.isRequired,
  eveningPhoneNumber: object,
  faxNumber: object,
  mobilePhoneNumber: object,
  name: string.isRequired
};

const MobilePeople = ({
  canViewBeneficiaries,
  canViewTrustedContacts,
  canViewContactInformation
}) => (
  <Fragment>
    <PreferencesRow>
      <PreferencesCol col={12}>
        {canViewContactInformation ? (
          <Link
            className="preferences-people-link"
            to="/profile/contact-information">
            <div>Contact Information</div>
            <ChevronRight className="preferences-chevron-right" />
          </Link>
        ) : (
          <span
            className="preferences-people-warning-text"
            dangerouslySetInnerHTML={{
              __html: Content.profileUpdateContactInfo
            }}
          />
        )}
      </PreferencesCol>
    </PreferencesRow>
    {canViewTrustedContacts && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <Link
            className="preferences-people-link"
            to="/profile/trusted-contacts">
            <div>Trusted Contacts</div>
            <ChevronRight className="preferences-chevron-right" />
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    {canViewBeneficiaries && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <Link className="preferences-people-link" to="/profile/beneficiaries">
            <div>Beneficiary on File</div>
            <ChevronRight className="preferences-chevron-right" />
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
  </Fragment>
);

MobilePeople.propTypes = {
  canViewBeneficiaries: bool.isRequired,
  canViewTrustedContacts: bool.isRequired
};

const PreferencesPeople = ({
  address,
  canViewBeneficiaries,
  canViewTrustedContacts,
  canViewContactInformation,
  daytimePhoneNumber,
  eveningPhoneNumber,
  email,
  faxNumber,
  isDesktopLayout,
  mobilePhoneNumber,
  name,
  view,
  hasResidential,
  residentialAddress
}) => (
  <div className="preferences-people">
    <PreferencesSectionHeader Icon={ProfileIcon} text="People" />
    {isDesktopLayout ? (
      <DesktopPeople
        address={address}
        canViewBeneficiaries={canViewBeneficiaries}
        canViewTrustedContacts={canViewTrustedContacts}
        canViewContactInformation={canViewContactInformation}
        daytimePhoneNumber={daytimePhoneNumber}
        eveningPhoneNumber={eveningPhoneNumber}
        email={email}
        faxNumber={faxNumber}
        mobilePhoneNumber={mobilePhoneNumber}
        name={name}
        view={view}
        hasResidential={hasResidential}
        residentialAddress={residentialAddress}
      />
    ) : (
      <MobilePeople
        canViewBeneficiaries={canViewBeneficiaries}
        canViewTrustedContacts={canViewTrustedContacts}
        canViewContactInformation={canViewContactInformation}
        view={view}
      />
    )}
  </div>
);

PreferencesPeople.propTypes = {
  address: object.isRequired,
  canViewBeneficiaries: bool.isRequired,
  canViewTrustedContacts: bool.isRequired,
  daytimePhoneNumber: object,
  email: object.isRequired,
  eveningPhoneNumber: object,
  faxNumber: object,
  mobilePhoneNumber: object,
  name: string.isRequired
};

PreferencesPeople.defaultProps = {
  address: {
    addressLines: []
  },
  email: {}
};

const mapStateToProps = (state) => ({
  address: getPrimaryMailingAddress(state),
  residentialAddress: getResidentialAddress(state),
  canViewBeneficiaries: canViewBeneficiaries(state),
  canViewTrustedContacts: canViewTrustedContacts(state),
  canViewContactInformation: canViewContactInformation(state),
  daytimePhoneNumber: getDaytimePhoneNumber(state),
  email: getPrimaryEmailAddress(state),
  eveningPhoneNumber: getEveningPhoneNumber(state),
  faxNumber: getFaxNumber(state),
  isDesktopLayout: state.view.isDesktopLayout,
  mobilePhoneNumber: getMobilePhoneNumber(state),
  name: getCustomerName(state),
  view: getView(state),
  hasResidential: hasResidentialAddress(state),
});

export default connect(mapStateToProps)(PreferencesPeople);
